import { ColDef, SuppressKeyboardEventParams, ValueSetterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { capitalize, isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
  KpiConfigPeriod,
  KpiPeriod,
  KpiSection,
} from '../../../../../data-models/company-financials.data-model';
import { IField } from '../../../../../data-models/field2.data-model';
import { FieldDefinition } from '../../../../../data-models/field-definition/field-definition.schema';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { kpiDefinitionByNameAtom, kpiGroupsByIdAtom } from '../../../../../services/state/KPIConfigState';
import { TemporalPlainDateFromJsDate } from '../../../../../util/date-utilts';
import { FMT } from '../../../../../util/formatter-service';
import { fiscalDateFormatter2, formatISODateOnly } from '../../../../../util/formatters/DateFormatters';
import { companyProfileFinancialsDefaultColDef } from '../../../commonGridColDefs';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import {
  financialsCurrencyOptionsForIdAtom,
  selectedFinancialsCurrencyForIdAtom,
} from './FinancialsCurrencyState';
import { FinancialsDisplaySettings } from '../components/FinancialsSettingsState';
import { KpiNameCellRenderer, KpiValueRenderer } from '../components/KpiCellRenderers';
import { KpiEditor } from '../components/KpiEditor';
import { usePinnedYearColDefs } from '../pinnedYears/usePinnedYearColDefs';
import { ICustomKPIActions, useBulkCustomKPIActions } from './useCustomKPIActions';
import { FinancialType, IFinancialsRowData2 } from './useFinancialsGridData';

export type IValuesBySection<T> = Record<FinancialType, T>;

export type HeaderCompParamsType = {
  colDate: Date;
  kpiPeriod: KpiPeriod;
};

export function isHeaderCompParams(params: unknown): params is HeaderCompParamsType {
  if (params == null || typeof params !== 'object') {
    return false;
  }
  const candidate = params as Record<string, unknown>;
  return candidate.colDate instanceof Date;
}

export interface IFinancialsColumnDefsParams {
  allPeriods: Array<Date>;
  frequency: KpiPeriod;
  options?: Partial<FinancialsDisplaySettings>;
}
export function useFinancialsColumnDefs({
  allPeriods,
  frequency,
  options = {},
}: IFinancialsColumnDefsParams) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId));
  const { kpiNameDef, kpiGroupDef } = useKpiColumnDefs(options);
  const getFinancialPeriodColDef = useGetFinancialPeriodColDef();

  const getPinnedYearDefs = usePinnedYearColDefs();
  const pinnedYearDefs = useMemo(() => {
    return getPinnedYearDefs({ fye: company?.fye ?? 12, options });
  }, [company?.fye, getPinnedYearDefs, options]);

  const columnDefs = useMemo(() => {
    if (!frequency) {
      return [];
    }
    const columnsDefs: ColDef<IFinancialsRowData2>[] = [
      kpiGroupDef,
      kpiNameDef,
    ] as ColDef<IFinancialsRowData2>[];

    const fye = company?.fye ?? 12;

    allPeriods.forEach((currentPeriod) => {
      const def = getFinancialPeriodColDef({ date: currentPeriod, frequency, fye, options });
      columnsDefs.push(def);
    });

    return [...columnsDefs, ...pinnedYearDefs];
  }, [
    frequency,
    kpiGroupDef,
    kpiNameDef,
    company?.fye,
    allPeriods,
    pinnedYearDefs,
    getFinancialPeriodColDef,
    options,
  ]);

  return {
    columnDefs,
    defaultColDef: companyProfileFinancialsDefaultColDef,
  };
}

export function kpiNameComparator(
  valueA: string,
  valueB: string,
  kpisByNameMap: Map<string, FieldDefinition>
) {
  return (
    (kpisByNameMap.get(valueA)?.sortOrder ?? Number.MAX_SAFE_INTEGER) -
    (kpisByNameMap.get(valueB)?.sortOrder ?? Number.MAX_SAFE_INTEGER)
  );
}

export type TKpiValueSetterParams = Pick<
  ValueSetterParams<IFinancialsRowData2, IValuesBySection<unknown>>,
  'data' | 'colDef' | 'newValue' | 'node' | 'api'
>;

export function useKpiValueSetter() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const currencyCode = useAtomValue(selectedFinancialsCurrencyForIdAtom(companyId));
  const bulkUpdateCustomKPIData = useBulkCustomKPIActions();

  return useCallback(
    (params: TKpiValueSetterParams) => {
      const { data, colDef, newValue } = params;
      const { colDate, kpiPeriod } = colDef.headerComponentParams as HeaderCompParamsType;
      const colTitle = colDef.headerName;
      const isoDate = formatISODateOnly(colDate);
      if (!data || !colTitle) {
        return false;
      }

      const actualFinancialsId = data?.valuesByDate2?.[isoDate]?.[kpiPeriod]?.actual?.id;
      const budgetFinancialsId = data?.valuesByDate2?.[isoDate]?.[kpiPeriod]?.budget?.id;

      const currentActual = data?.valuesByDate2?.[isoDate]?.[kpiPeriod]?.actual?.value;
      const currentBudget = data?.valuesByDate2?.[isoDate]?.[kpiPeriod]?.budget?.value;

      if (isEqual(newValue?.actual, currentActual) && isEqual(newValue?.budget, currentBudget)) {
        return false;
      }

      const payload = {
        companyId,
        date: isoDate,
        kpiId: data.id,
        period: kpiPeriod,
        currencyCode,
      };

      const actions: ICustomKPIActions = {
        companyId,
        create: [],
        update: [],
        delete: [],
      };

      if (newValue?.actual == null && currentActual != null && actualFinancialsId) {
        actions.delete.push([companyId, actualFinancialsId]);
      }

      if (newValue?.budget == null && currentBudget != null && budgetFinancialsId) {
        actions.delete.push([companyId, budgetFinancialsId]);
      }

      if (newValue?.actual != null && !isEqual(newValue.actual, currentActual)) {
        const payloadValue = toPayloadValue(newValue.actual);
        if (actualFinancialsId) {
          actions.update.push([
            companyId,
            actualFinancialsId,
            { date: payload.date, value: payloadValue, section: KpiSection.actual },
          ]);
        } else {
          actions.create.push([companyId, { ...payload, value: payloadValue, section: KpiSection.actual }]);
        }
      }

      if (newValue?.budget != null && !isEqual(newValue.budget, currentBudget)) {
        const payloadValue = toPayloadValue(newValue.budget);
        if (budgetFinancialsId) {
          actions.update.push([
            companyId,
            budgetFinancialsId,
            { date: payload.date, value: payloadValue, section: KpiSection.budget },
          ]);
        } else {
          actions.create.push([companyId, { ...payload, value: payloadValue, section: KpiSection.budget }]);
        }
      }

      if (!actions.create.length && !actions.update.length && !actions.delete.length) {
        return false;
      }
      bulkUpdateCustomKPIData(actions);
      return true;
    },
    [bulkUpdateCustomKPIData, companyId, currencyCode]
  );
}

export function toPayloadValue(value: unknown) {
  if (value instanceof Date) {
    return TemporalPlainDateFromJsDate(value).toString();
  }
  return String(value);
}

export function suppressNavigation(params: SuppressKeyboardEventParams<IFinancialsRowData2>) {
  const {
    event: { key },
    editing,
  } = params;
  return editing && (key === 'Tab' || key === 'Enter');
}

export function useKpiColumnDefs(options: IFinancialsColumnDefsParams['options'] = {}) {
  const kpisByNameMap = useAtomValue(kpiDefinitionByNameAtom);
  const kpiGroupsById = useAtomValue(kpiGroupsByIdAtom);
  // The grouped row data carries `kpiGroupId` (it's the row's `IField`-shaped
  // financials row, see `IFinancialsRowData2`). Phase 5 leaves the row-data
  // shape untouched (write paths still construct it); only the display reads
  // through `kpisByNameMap` are migrated.
  const kpiGroupDef: ColDef<IField<unknown>> = useMemo(() => {
    return {
      colId: 'kpiGroupId',
      field: 'kpiGroupId',
      headerName: 'KPI Group',
      hide: true,
      initialSort: 'asc',
      rowGroup: true,
      valueGetter: (params) => {
        return kpiGroupsById.get(Number(params.data?.kpiGroupId ?? -1))?.name ?? '-';
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        // Get group ID from the first child's data in each group node
        const groupIdA = nodeA.allLeafChildren?.[0]?.data?.kpiGroupId;
        const groupIdB = nodeB.allLeafChildren?.[0]?.data?.kpiGroupId;

        const groupA = groupIdA !== undefined ? kpiGroupsById.get(Number(groupIdA)) : undefined;
        const groupB = groupIdB !== undefined ? kpiGroupsById.get(Number(groupIdB)) : undefined;

        const indexA = groupA?.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const indexB = groupB?.sortOrder ?? Number.MAX_SAFE_INTEGER;

        return indexA - indexB;
      },
    };
  }, [kpiGroupsById]);

  const kpiNameDef: ColDef<IFinancialsRowData2> = useMemo(() => {
    return {
      colId: 'kpiName',
      field: 'displayName',
      filter: true,
      suppressHeaderFilterButton: false,
      suppressHeaderContextMenu: false,
      suppressHeaderMenuButton: false,
      filterParams: {
        buttons: ['reset'],
        comparator: (valueA?: string, valueB?: string) => {
          return valueA?.localeCompare(valueB ?? '', undefined, { sensitivity: 'base' }) ?? 0;
        },
      },
      headerName: 'KPI Name',
      initialSort: 'asc',
      minWidth: 250,
      comparator: (valueA, valueB) => {
        return kpiNameComparator(valueA ?? '', valueB ?? '', kpisByNameMap);
      },
      pinned: 'left',
      sortable: true,
      cellRenderer: KpiNameCellRenderer,
      cellStyle: { padding: 0 },
      cellRendererParams: { ...options },
    };
  }, [kpisByNameMap, options]);

  return {
    kpiGroupDef,
    kpiNameDef,
  };
}

interface ICommonFinancialsColumnDefsParams extends Pick<
  IFinancialsColumnDefsParams,
  'frequency' | 'options'
> {
  date: Date;
  fye: number; //[1-12]
}
export function useGetFinancialPeriodColDef() {
  const hasAuditorAccess = PermissionService.get().hasPermission(PermissionKey.hasAuditorAccess);
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const selectedCurrency = useAtomValue(selectedFinancialsCurrencyForIdAtom(companyId));
  const currencyOptions = useAtomValue(financialsCurrencyOptionsForIdAtom(companyId));
  // Editing is only safe in the native currency; a converted view is read-only
  // (conversion must not change underlying values). options[0] is the native currency.
  const isNativeCurrency = selectedCurrency === currencyOptions[0];

  return useCallback(
    ({
      date,
      frequency,
      fye,
      options = {},
    }: ICommonFinancialsColumnDefsParams): ColDef<IFinancialsRowData2> => {
      const isoDate = formatISODateOnly(date);
      const colTitle = fiscalDateFormatter2({ period: frequency, periodISODate: isoDate, fye });

      return {
        headerName: colTitle,
        editable: !hasAuditorAccess && isNativeCurrency,
        valueGetter: (params) => {
          const { valuesByDate2 } = params.data ?? {};
          const valuesForDate = valuesByDate2?.[isoDate];

          return {
            actual: valuesForDate?.[frequency]?.[KpiSection.actual]?.value ?? null,
            budget: valuesForDate?.[frequency]?.[KpiSection.budget]?.value ?? null,
            growth: valuesForDate?.[`Growth${capitalize(frequency)}`]?.[KpiSection.actual]?.value ?? null,
            [KpiConfigPeriod.LTM]: valuesForDate?.[KpiConfigPeriod.LTM]?.[KpiSection.actual]?.value ?? null,
            [KpiConfigPeriod.CYTD]: valuesForDate?.[KpiConfigPeriod.CYTD]?.[KpiSection.actual]?.value ?? null,
            // Per-value FX resolution: a converted value the BE couldn't price is
            // flagged so the renderer shows an indicator instead of a wrong number.
            currencyUnresolved: {
              actual: valuesForDate?.[frequency]?.[KpiSection.actual]?.currencyUnresolved ?? false,
              budget: valuesForDate?.[frequency]?.[KpiSection.budget]?.currencyUnresolved ?? false,
              [KpiConfigPeriod.LTM]:
                valuesForDate?.[KpiConfigPeriod.LTM]?.[KpiSection.actual]?.currencyUnresolved ?? false,
              [KpiConfigPeriod.CYTD]:
                valuesForDate?.[KpiConfigPeriod.CYTD]?.[KpiSection.actual]?.currencyUnresolved ?? false,
            },
          };
        },
        cellRenderer: KpiValueRenderer,
        cellRendererParams: { ...options, fullWidth: true },
        cellEditor: !hasAuditorAccess ? KpiEditor : undefined,
        cellStyle: { padding: 0 },
        cellEditorPopup: true,
        cellEditorParams: {
          fye,
        },
        headerComponentParams: {
          colDate: date,
          kpiPeriod: frequency,
        },
        headerClass: 'centered-header',
        suppressKeyboardEvent: (params) => {
          return suppressNavigation(params);
        },
      };
    },
    [hasAuditorAccess, isNativeCurrency]
  );
}

export function getFinancialsFromRowData(data: IFinancialsRowData2, isoDate: string, kpiPeriod: KpiPeriod) {
  return data?.valuesByDate2?.[isoDate]?.[kpiPeriod];
}

export function getPeriodColumnLabel(isoDateOnlyString: string, frequency: KpiPeriod) {
  const date = new Date(isoDateOnlyString);

  switch (frequency.toLowerCase()) {
    case 'quarter':
    case 'month': {
      return FMT.format('monthYear', date);
    }
    case 'year':
      return FMT.format('YYYY', new Date(isoDateOnlyString));
    default:
      return isoDateOnlyString;
  }
}
